import React, { useState } from "react";
import { Link } from "react-router-dom";
import PageTopSlider from "../components/PageTopSlider";
import ReviewsSlider from "../components/ReviewsSlider";
import ContactForm from "../components/ContactForm";
import SEO from "../components/SEO";

export default function Fugtmaaling() {
  const [openFaq, setOpenFaq] = useState(null);

  const fugtFaqs = [
    {
      q: "Hvad er en ikke-destruktiv fugtmåling?",
      a: "Instrumentet sender et svagt elektrisk signal ind i materialet uden at bore eller skære. Fugt ændrer signalet, så vi kan se, hvor der er fugt, og hvor meget det afviger fra et tørt område. Målingen er en indikation – ikke et laboratorietal.",
    },
    {
      q: "Skal I bore huller?",
      a: "Som udgangspunkt nej. Tage, vægge og betongulve scannes gennem overfladen. I træ og gips bruger vi en nåleprobe, som efterlader to små huller. Skal der dokumenteres en RF-værdi i beton, kræver det borehuller – det er en anden metode, som vi gerne rådgiver om.",
    },
    {
      q: "Kan I finde fugt i et fladt tag?",
      a: "Ja. Vi scanner tagfladen og finder de områder, hvor der er fugt under tagpappen eller i isoleringen. Tagfladen skal være tør på overfladen, og metoden virker ikke gennem metal eller på visse gummimembraner (EPDM og butyl).",
    },
    {
      q: "Kan I måle, om mit betongulv er klar til gulvlægning?",
      a: "Vi kan screene beton og afretningslag ikke-destruktivt i de øverste ca. 2 cm og finde de fugtigste områder. Kræver gulvleverandøren en dokumenteret RF-værdi, skal der laves en RF-måling i borehuller. Vi fortæller dig, hvad der skal til i din sag.",
    },
    {
      q: "Er en fugtmåling det samme som en skimmelundersøgelse?",
      a: "Nej. Fugtmålingen viser, hvor der er fugt, som kan give grobund for skimmel. Den kan ikke vise, om der er skimmel, men den kan pege på, hvor en eventuel skimmelprøve bør tages.",
    },
    {
      q: "Hvad koster en fugtmåling?",
      a: "En fugtmåling i et enfamiliehus koster fra 5.000 kr. inkl. moms. For flerfamiliehuse og erhvervs- og industribygninger er prisen fra 5.000 kr. + moms, og den endelige pris aftales efter tilbud. Scanning af flade tage er efter tilbud, fordi prisen afhænger af tagfladens størrelse og adgangen. Du kender prisen, før vi går i gang – er opgaven større end normalt, aftaler vi den endelige pris inden besøget.",
    },
    {
      q: "Hvad får jeg efter besøget?",
      a: "Du får en mundtlig gennemgang på stedet og derefter en rapport med målepunkter, fotos, aflæste værdier og vores vurdering af, hvad der bør ske nu. Rapporten sendes inden for 2–5 hverdage efter målingen.",
    },
    {
      q: "Kan fugtmålingen kombineres med termografi eller trykprøve?",
      a: "Ja. Termografi viser temperaturforskelle på overfladen, som kan skyldes fugt, kuldebroer eller utætheder – fugtmålingen afklarer, om der faktisk er fugt. Ved en trykprøve finder vi utæthederne, og fugtmålingen viser, om der er trængt fugt ind i konstruktionen. Bestiller du begge dele, kan vi lave dem ved samme besøg.",
    },
    {
      q: "Kan jeg bruge en fugtmåling, når jeg køber bolig?",
      a: "Ja, som et supplement. Er du i tvivl om et konkret område, fx en kælder, et badeværelse eller et tag, kan vi måle det, før du skriver under. Fugtmålingen erstatter ikke tilstandsrapporten.",
    },
    {
      q: "Hvor kommer I ud?",
      a: "Vi dækker hele Jylland og Fyn og har base i Horsens.",
    },
  ];

  const fugtServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "@id": "https://www.etdanmark.com/fugtmaaling#service",
    name: "Fugtmåling med Tramex",
    alternateName: "Fugtmåling af tag, vægge, gulve og træ",
    serviceType: "Fugtmåling i bygninger",
    description:
      "Fugtmåling i flade tage, vægge, facader, betongulve, afretningslag og træ med professionelt Tramex-fugtmåleudstyr. Primært ikke-destruktiv scanning suppleret med nålemåling i træ og måling af luftfugtighed, temperatur og dugpunkt. Rapport med målepunkter, fotos og vurdering.",
    url: "https://www.etdanmark.com/fugtmaaling",
    offers: {
      "@type": "Offer",
      url: "https://www.etdanmark.com/fugtmaaling",
      priceCurrency: "DKK",
      priceSpecification: {
        "@type": "PriceSpecification",
        minPrice: 5000,
        priceCurrency: "DKK",
        valueAddedTaxIncluded: true,
      },
      description:
        "Fugtmåling i enfamiliehus fra 5.000 kr. inkl. moms (4.000 kr. + moms). Flerfamiliehuse og erhvervs-/industribygninger fra 5.000 kr. + moms – endelig pris efter tilbud. Scanning af flade tage efter tilbud. Rapport inden for 2–5 hverdage efter målingen.",
    },
    areaServed: [
      {
        "@type": "AdministrativeArea",
        name: "Jylland",
      },
      {
        "@type": "AdministrativeArea",
        name: "Fyn",
      },
    ],
    provider: {
      "@type": "LocalBusiness",
      "@id": "https://www.etdanmark.com/#organization",
      name: "ET Danmark ApS",
      url: "https://www.etdanmark.com/",
      telephone: "+4542499595",
      email: "info@etdanmark.com",
      vatID: "DK41026138",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Bjerrevej 62 A",
        postalCode: "8700",
        addressLocality: "Horsens",
        addressCountry: "DK",
      },
    },
  };

  const fugtFaqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": "https://www.etdanmark.com/fugtmaaling#faq",
    mainEntity: fugtFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  const measurementAreas = [
    {
      title: "Flade tage",
      desc: "Fugt under tagpap og membraner og i isoleringen. Store tagflader scannes systematisk, og målingen når op til ca. 15 cm ned i tagopbygningen.",
      icon: "fa-layer-group",
    },
    {
      title: "Vægge og facader",
      desc: "Fx pudsede facader på isolering, gipsvægge og andre konstruktioner. Scanneren måler op til ca. 10 cm ind i materialet og hjælper med at spore, hvor vandet kommer ind.",
      icon: "fa-border-all",
    },
    {
      title: "Betongulve og afretningslag",
      desc: "Ikke-destruktiv screening af de øverste ca. 2 cm, også på anhydrit- og gipsbaserede afretningslag.",
      icon: "fa-square",
    },
    {
      title: "Træværk og konstruktioner",
      desc: "Spær, bjælker, bundrem, gulvbrædder og andet træværk. Med en nåleprobe med isolerede nåle måler vi træets fugtindhold i procent, også et stykke inde i træet.",
      icon: "fa-tree",
    },
    {
      title: "Indeklima & kondensrisiko",
      desc: "Luftfugtighed, temperatur og dugpunkt i rummet samt overfladetemperatur, så vi kan vurdere risikoen for kondens.",
      icon: "fa-temperature-half",
    },
  ];

  const relevantSituations = [
    {
      title: "Nybyggeri før gulvlægning",
      desc: "Er der våde områder i betonen eller afretningslaget, som skal undersøges nærmere, før der lægges trægulv, vinyl eller klinker?",
      icon: "fa-person-digging",
    },
    {
      title: "Efter en vandskade",
      desc: "Hvor langt har vandet bredt sig i gulv, vægge og træ, og tørrer konstruktionen ud som planlagt?",
      icon: "fa-water",
    },
    {
      title: "Utæt fladt tag",
      desc: "Hvor er der vand under tagpappen eller i isoleringen, og er det hele taget eller kun et afgrænset område, der skal udbedres?",
      icon: "fa-cloud-rain",
    },
    {
      title: "Fugtig kælder",
      desc: "Er fugten i væggene og gulvet, eller er det kondens fra rumluften? Måling af både materialer og indeklima hjælper med at skelne.",
      icon: "fa-house-crack",
    },
    {
      title: "Badeværelse",
      desc: "Mistanke om en utæthed ved bruseniche eller afløb. Vi måler i de tilstødende vægge, gulve og træværk og finder, hvor fugten breder sig.",
      icon: "fa-shower",
    },
    {
      title: "Køb og salg af bolig",
      desc: "Få målt et konkret område, du er i tvivl om, før du skriver under. Fugtmålingen er et værdifuldt supplement til tilstandsrapporten, ikke en erstatning.",
      icon: "fa-handshake",
    },
    {
      title: "Mistanke om skimmel",
      desc: "Målingen viser, hvor der er fugt, som kan give grobund for skimmel. Den er ikke en skimmelanalyse, men kan pege på, hvor en eventuel skimmelprøve bør tages.",
      icon: "fa-biohazard",
    },
    {
      title: "Før en konstruktion lukkes",
      desc: "Kontrol af, at træ og isolering er tørt, før der lukkes med plader, dampspærre eller beklædning.",
      icon: "fa-shield-halved",
    },
  ];

  const equipmentList = [
    {
      name: "Tramex DEC tagskanner",
      desc: "Skanner på hjul til store, flade tage. Søger fugt under tagpap og membraner og i isoleringen, op til ca. 15 cm ned.",
      badge: "Op til 15 cm dybde",
    },
    {
      name: "Tramex RWS tag- og vægskanner",
      desc: "Til tage, facader og vægge, op til ca. 10 cm ind i materialet. Bruges også målrettet til at spore utætheder.",
      badge: "Op til 10 cm dybde",
    },
    {
      name: "Tramex CMEX5 fugtmåler",
      desc: "Ikke-destruktiv måling i beton og afretningslag (ca. 2 cm ned) og indbygget måling af luftfugtighed, temperatur og dugpunkt. Med nåleprobe måler den fugtindholdet i træ og gips.",
      badge: "Multifunktionelt præcisionsudstyr",
    },
    {
      name: "Infrarødt termometer",
      desc: "Til overfladetemperatur, så vi præcist kan sammenholde med dugpunktet og vurdere den reelle risiko for kondens.",
      badge: "Overfladeanalyse",
    },
  ];

  const processSteps = [
    {
      num: "01",
      title: "Uforpligtende henvendelse",
      text: "Fortæl os, hvad du har set – fx skjolder, lugt, en vandskade eller et nyt betongulv – og send gerne et par fotos. Vi vurderer opgaven og sender en fast pris, så du kender prisen, før vi går i gang.",
    },
    {
      num: "02",
      title: "Besøg og måling",
      text: "Vi starter med en referencemåling på et tørt område og scanner derefter de berørte flader systematisk. Områder, der afviger, markeres og undersøges nærmere, fx med nåleprobe i træet. Vi måler også luftfugtighed, temperatur og dugpunkt.",
    },
    {
      num: "03",
      title: "Gennemgang på stedet",
      text: "Inden vi kører, gennemgår vi kort sammen, hvad vi har fundet, og hvad resultaterne betyder for bygningen.",
    },
    {
      num: "04",
      title: "Rapport med anbefalinger",
      text: "Du får en officiel rapport med målepunkter, fotos, aflæste værdier og vores vurdering af næste skridt – fx udbedring, udtørring, en kontrolmåling senere eller en RF-måling i borehuller. Rapporten sendes på mail inden for 2–5 hverdage efter målingen.",
    },
  ];

  return (
    <div className="bg-[#FAF9F6] text-stone-800">
      <SEO
        title="Fugtmåling med Tramex i Jylland & Fyn | ET Danmark"
        description="Fugtmåling i tag, vægge, gulve og træ med professionelt Tramex-udstyr. Fra 5.000 kr. inkl. moms. Rapport inden for 2–5 hverdage. Jylland & Fyn."
        canonical="https://www.etdanmark.com/fugtmaaling"
        ogImage="https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07589.webp"
        schemas={[fugtServiceSchema, fugtFaqSchema]}
      />

      {/* 1. TOP HERO */}
      <PageTopSlider
        title="Fugtmåling med Tramex – find fugten, før den bliver en skade"
        subtitle="Fugt i en bygning er sjældent til at se, før skaden er sket. Med en professionel fugtmåling finder vi ud af, om – og hvor – der er fugt i tag, vægge, gulve og træværk. Hurtigt, ikke-destruktivt og med klar rapport inden for 2–5 hverdage."
        badge="Professionel Tramex-måling"
        breadcrumbs={[
          { label: "Forside", to: "/" },
          { label: "Ydelser", to: "/#ydelser" },
          { label: "Fugtmåling" },
        ]}
        priceBadge="Fra 5.000 kr. inkl. moms"
        actions={
          <>
            <a
              href="#kontakt"
              className="px-6 py-3 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm shadow-md transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-droplet text-xs" aria-hidden="true"></i>
              <span>Få et uforpligtende tilbud på fugtmåling</span>
            </a>
            <a
              href="tel:+4542499595"
              className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-all flex items-center gap-2"
            >
              <i className="fa-solid fa-phone text-emerald-300 text-xs" aria-hidden="true"></i>
              <span>Ring til Lars: 42 49 95 95</span>
            </a>
          </>
        }
        image={{
          url: "https://assets.auxodev.dk/assets/769d49b2-5afc-461a-944c-d9cca711718f/dsc07546.jpg",
          alt: "Fugtmåling i bygning med professionelt Tramex udstyr",
        }}
      />

      {/* 2. INTRO & DET KAN VI MÅLE */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center mb-16">
            <div className="lg:col-span-7 space-y-5">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100/80 text-[#165B4C] text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-droplet text-emerald-600" aria-hidden="true"></i>
                <span>Ikke-destruktiv bygningsinspektion</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight leading-tight">
                Mål fugten med professionelt Tramex-udstyr
              </h2>
              <div className="space-y-4 text-stone-600 text-sm sm:text-base leading-relaxed">
                <p>
                  Fugt i en bygning er sjældent til at se, før skaden er sket. Med en fugtmåling finder vi ud af, om – og hvor – der er fugt i tag, vægge, gulve og træværk. Så kan du tage stilling, før der lægges nyt gulv, lukkes en konstruktion eller skrives under på en bolighandel.
                </p>
                <p>
                  Vi måler med professionelt fugtmåleudstyr fra Tramex. Det meste af målingen er ikke-destruktiv: Vi scanner gennem overfladen uden at bore eller skære. Du får en rapport med målepunkter, fotos og en klar vurdering inden for 2–5 hverdage efter målingen. Vi dækker hele Jylland og Fyn.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap gap-4">
                <a
                  href="#kontakt"
                  className="px-6 py-3 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white font-bold text-sm shadow-sm transition-all inline-flex items-center gap-2"
                >
                  <i className="fa-solid fa-envelope text-xs" aria-hidden="true"></i>
                  <span>Få et uforpligtende tilbud</span>
                </a>
                <a
                  href="tel:+4542499595"
                  className="px-6 py-3 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-800 font-bold text-sm transition-all inline-flex items-center gap-2 border border-stone-200"
                >
                  <i className="fa-solid fa-phone text-[#165B4C] text-xs" aria-hidden="true"></i>
                  <span>Ring: 42 49 95 95</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-[#FAF9F6] p-6 sm:p-8 rounded-3xl border border-stone-200/80 shadow-md space-y-6">
                <h3 className="text-xl font-bold text-stone-900 flex items-center gap-2.5">
                  <span className="w-8 h-8 rounded-xl bg-[#165B4C] text-white flex items-center justify-center text-sm shrink-0">
                    <i className="fa-solid fa-check" aria-hidden="true"></i>
                  </span>
                  <span>Kort overblik over fordelene</span>
                </h3>
                <ul className="space-y-3.5 text-sm text-stone-700">
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                    <span><strong>Ingen unødige huller:</strong> Ikke-destruktiv scanning af overflader uden at skære eller bore.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                    <span><strong>Præcise Tramex-scannere:</strong> Måler op til ca. 15 cm dybde i tag og 10 cm i vægge.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                    <span><strong>Hurtig levering:</strong> Komplet rapport med målepunkter, fotos og vurdering inden for 2–5 hverdage.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="fa-solid fa-circle-check text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                    <span><strong>Én rådgiver:</strong> Kan med fordel kombineres med trykprøvning og termografi ved samme besøg.</span>
                  </li>
                </ul>

                <div className="pt-4 border-t border-stone-200 flex items-center justify-between text-xs text-stone-500">
                  <span>Fast pris kendt på forhånd</span>
                  <span className="font-bold text-[#165B4C]">Jylland & Fyn</span>
                </div>
              </div>
            </div>
          </div>

          {/* 1.3 DET KAN VI MÅLE */}
          <div className="mt-12">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <h3 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
                Det kan vi måle i din bygning
              </h3>
              <p className="text-stone-600 text-sm mt-2">
                Med markedets bedste Tramex-udstyr kortlægger vi fugtforhold i alle centrale bygningsdele:
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {measurementAreas.map((area, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-2xl bg-[#FAF9F6] border border-stone-200/80 hover:border-emerald-300 transition-all flex flex-col justify-between shadow-2xs group"
                >
                  <div>
                    <div className="w-12 h-12 rounded-xl bg-[#165B4C]/10 text-[#165B4C] flex items-center justify-center text-lg mb-4 group-hover:bg-[#165B4C] group-hover:text-white transition-colors">
                      <i className={`fa-solid ${area.icon}`} aria-hidden="true"></i>
                    </div>
                    <h4 className="text-lg font-bold text-stone-900 mb-2">{area.title}</h4>
                    <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">{area.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3. IKKE-DESTRUKTIV MÅLING OG BEGRÆNSNINGER (1.4) */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="max-w-3xl mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider mb-2">
              <i className="fa-solid fa-microscope" aria-hidden="true"></i>
              <span>Metoder og teknik</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Ikke-destruktiv måling – og hvad den ikke er
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2 leading-relaxed">
              Få en ærlig og saglig gennemgang af, hvordan målingerne udføres, og hvilken målemetode der er rigtig til din situation:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Metodetype 1 */}
            <div className="bg-white p-7 rounded-2xl border border-stone-200 shadow-2xs flex flex-col justify-between">
              <div>
                <div className="inline-block px-2.5 py-1 rounded-md text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 mb-4">
                  Primær metode
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-2">
                  Ikke-destruktiv scanning
                </h3>
                <p className="text-xs sm:text-sm text-stone-600 leading-relaxed space-y-2">
                  <span>
                    Instrumentet sender et svagt elektrisk signal ind i materialet og måler, hvordan det ændrer sig med fugtindholdet. Der bores ikke, og overfladen tager ikke skade.
                  </span>
                  <span className="block mt-2">
                    På tage og vægge er værdierne sammenlignende: Vi måler først et område, vi ved er tørt, og finder derefter de steder, der afviger. Målingen viser, hvor fugten er, og hvor meget den afviger – den er en indikation og ikke et laboratorietal.
                  </span>
                </p>
              </div>
            </div>

            {/* Metodetype 2 */}
            <div className="bg-white p-7 rounded-2xl border border-stone-200 shadow-2xs flex flex-col justify-between">
              <div>
                <div className="inline-block px-2.5 py-1 rounded-md text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200 mb-4">
                  Lille indgreb
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-2">
                  Nålemåling i træ og gips
                </h3>
                <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                  I træ og gips trykkes to tynde nåle ind i materialet. Det efterlader to små huller og giver fugtindholdet i procent. Anvendes målrettet i spær, bjælker, bundrem og andet træværk, hvor en præcis fugtprocent er nødvendig for at vurdere risikoen for råd og svamp.
                </p>
              </div>
            </div>

            {/* Metodetype 3 */}
            <div className="bg-white p-7 rounded-2xl border border-stone-200 shadow-2xs flex flex-col justify-between">
              <div>
                <div className="inline-block px-2.5 py-1 rounded-md text-xs font-bold bg-stone-100 text-stone-800 border border-stone-300 mb-4">
                  Borehuller
                </div>
                <h3 className="text-lg font-bold text-stone-900 mb-2">
                  RF-måling i beton
                </h3>
                <p className="text-xs sm:text-sm text-stone-600 leading-relaxed">
                  Kræver en gulvleverandør eller entreprenør en dokumenteret relativ fugtighed (RF) i betonen, skal der bores huller og sættes målere ned i betonen, som skal stå en periode, før de aflæses. Det er en anden metode end den ikke-destruktive screening. Vi rådgiver gerne om, hvornår det er nødvendigt.
                </p>
              </div>
            </div>
          </div>

          {/* Begrænsninger boks */}
          <div className="mt-8 p-6 rounded-2xl bg-amber-50/70 border border-amber-200/80 text-amber-950 flex flex-col sm:flex-row gap-4 items-start">
            <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center shrink-0 text-lg">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
            </div>
            <div className="text-xs sm:text-sm leading-relaxed">
              <h4 className="font-bold text-amber-900 text-sm mb-1">Begrænsninger at være opmærksom på:</h4>
              <p>
                Målingen virker ikke gennem metal (fx stålplader og alu-beklædning) og ikke på visse gummibaserede tagmembraner (EPDM og butyl). Tagflader skal være tørre på overfladen, når vi scanner.
              </p>
              <p className="mt-2 font-medium">
                <strong>Kort sagt:</strong> Den ikke-destruktive måling viser hurtigt, om og hvor der er fugt. Er der brug for et dokumenteret tal, fortæller vi dig ærligt, hvilken metode der skal til.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. HVORNÅR ER EN FUGTMÅLING RELEVANT? (1.5) */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider mb-2">
              <i className="fa-solid fa-clipboard-check" aria-hidden="true"></i>
              <span>Typiske situationer</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Hvornår er en fugtmåling relevant?
            </h2>
            <p className="text-stone-600 text-sm sm:text-base mt-2">
              Uanset om du bygger nyt, renoverer, har haft en skade eller er ved at købe bolig:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {relevantSituations.map((item, idx) => (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-[#FAF9F6] border border-stone-200/80 hover:border-emerald-300 transition-all flex flex-col justify-between"
              >
                <div>
                  <div className="w-10 h-10 rounded-xl bg-emerald-100/70 text-[#165B4C] flex items-center justify-center text-base mb-3">
                    <i className={`fa-solid ${item.icon}`} aria-hidden="true"></i>
                  </div>
                  <h3 className="font-bold text-stone-900 text-base mb-2">{item.title}</h3>
                  <p className="text-xs text-stone-600 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA MIDT PÅ SIDEN (1.11) */}
          <div className="mt-12 p-8 sm:p-10 rounded-3xl bg-[#165B4C] text-white flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
            <div className="space-y-2 max-w-2xl">
              <span className="inline-block px-3 py-1 rounded-full bg-white/20 text-white text-xs font-semibold backdrop-blur-xs">
                Hurtig afklaring
              </span>
              <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight">
                Har du haft en vandskade, eller skal der lægges gulv?
              </h3>
              <p className="text-emerald-100 text-xs sm:text-sm leading-relaxed">
                Send os et par fotos og en kort beskrivelse af projektet, så vender vi tilbage med en fast pris og hurtig udrykning.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
              <a
                href="#kontakt"
                className="px-6 py-3 rounded-full bg-white text-[#165B4C] hover:bg-stone-100 font-bold text-sm shadow-md transition-colors text-center"
              >
                Indhent tilbud nu
              </a>
              <a
                href="tel:+4542499595"
                className="px-6 py-3 rounded-full bg-white/15 hover:bg-white/25 border border-white/30 text-white font-bold text-sm backdrop-blur-xs transition-colors text-center"
              >
                Ring: 42 49 95 95
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PROCESSEN - TRIN FOR TRIN (1.6) */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider mb-2">
              <i className="fa-solid fa-list-ol" aria-hidden="true"></i>
              <span>Gennemskuelig proces</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Sådan foregår det – fra henvendelse til rapport
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Vi gør det nemt og hurtigt for dig at få klarhed over fugtforholdene:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, idx) => (
              <div
                key={idx}
                className="p-6 rounded-2xl bg-white border border-stone-200 shadow-2xs flex flex-col justify-between relative"
              >
                <div>
                  <span className="text-3xl font-black text-emerald-600/30 block mb-2 font-mono">
                    {step.num}
                  </span>
                  <h3 className="text-base font-bold text-stone-900 mb-2">{step.title}</h3>
                  <p className="text-xs text-stone-600 leading-relaxed">{step.text}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-5 rounded-2xl bg-white border border-stone-200 text-xs sm:text-sm text-stone-600 flex items-center gap-3">
            <i className="fa-solid fa-circle-info text-emerald-600 text-base shrink-0" aria-hidden="true"></i>
            <div>
              <strong>Det skal du gøre klar:</strong> Adgang til de rum og flader, der skal måles. Ved tagmåling: sikker adgang til taget samt en tør tagflade.
            </div>
          </div>
        </div>
      </section>

      {/* 6. DET FÅR DU (1.7) & VORES UDSTYR (1.8) */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Det får du */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-gift" aria-hidden="true"></i>
                <span>Komplet leverance</span>
              </div>
              <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight">
                Det får du hos ET Danmark
              </h2>
              <ul className="space-y-3.5 text-sm text-stone-700">
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check-circle text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span>Fugtmåling med professionelt Tramex-udstyr</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check-circle text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span>Ikke-destruktiv scanning af tag, vægge og gulve, hvor det er muligt</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check-circle text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span>Nålemåling af fugtindhold i træ, hvor det er relevant</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check-circle text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span>Måling af luftfugtighed, temperatur, dugpunkt og overfladetemperatur</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check-circle text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span>En rapport med målepunkter, fotos og en klar vurdering – inden for 2–5 hverdage efter målingen</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check-circle text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span>Konkrete anbefalinger til næste skridt og udbedring</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check-circle text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span>Du kender den faste pris, før vi går i gang</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="fa-solid fa-check-circle text-emerald-600 mt-1 shrink-0" aria-hidden="true"></i>
                  <span>Én fast kontaktperson, der også laver termografi, trykprøve og energimærke</span>
                </li>
              </ul>
            </div>

            {/* Vores udstyr */}
            <div className="lg:col-span-6 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-toolbox" aria-hidden="true"></i>
                <span>Avanceret teknologi</span>
              </div>
              <h2 className="text-3xl font-extrabold text-stone-900 tracking-tight">
                Vores professionelle Tramex-udstyr
              </h2>
              <div className="space-y-4">
                {equipmentList.map((eq, idx) => (
                  <div
                    key={idx}
                    className="p-4 rounded-xl bg-[#FAF9F6] border border-stone-200 flex flex-col justify-between"
                  >
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <h4 className="font-bold text-stone-900 text-sm">{eq.name}</h4>
                      <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-emerald-100 text-emerald-800">
                        {eq.badge}
                      </span>
                    </div>
                    <p className="text-xs text-stone-600 leading-relaxed">{eq.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. PRISOVERSIGT (1.9) */}
      <section className="py-16 sm:py-20 bg-stone-50 border-b border-stone-200/70" id="priser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider mb-2">
              <i className="fa-solid fa-tags" aria-hidden="true"></i>
              <span>Faste og gennemskuelige priser</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Priser på Fugtmåling
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Du kender altid prisen, før vi kører ud. Rapporten sendes inden for 2–5 hverdage efter målingen.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {/* Pristabel */}
            <div className="bg-white rounded-3xl border border-stone-200 shadow-md overflow-hidden mb-8">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#165B4C] text-white">
                    <tr>
                      <th className="py-4 px-6 font-semibold">Ydelse</th>
                      <th className="py-4 px-6 font-semibold">Vejledende pris</th>
                      <th className="py-4 px-6 font-semibold text-right">Handling</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-200">
                    <tr className="hover:bg-stone-50/80 transition-colors">
                      <td className="py-4 px-6">
                        <div className="font-bold text-stone-900">
                          Fugtmåling i enfamiliehus
                        </div>
                        <div className="text-xs text-stone-500">
                          Fx vandskade, kælder, badeværelse, gulv
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-extrabold text-[#165B4C] text-base">
                          Fra 5.000 kr. inkl. moms
                        </span>
                        <div className="text-xs text-stone-500">(4.000 kr. + moms)</div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <a
                          href="#kontakt"
                          className="px-4 py-1.5 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white text-xs font-bold transition-colors inline-block"
                        >
                          Få tilbud
                        </a>
                      </td>
                    </tr>
                    <tr className="hover:bg-stone-50/80 transition-colors">
                      <td className="py-4 px-6">
                        <div className="font-bold text-stone-900">
                          Flerfamiliehuse og erhvervs-/industribygninger
                        </div>
                        <div className="text-xs text-stone-500">
                          Etageejendomme, kontorer, lager og produktion
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-extrabold text-[#165B4C] text-base">
                          Fra 5.000 kr. + moms
                        </span>
                        <div className="text-xs text-stone-500">Endelig pris efter tilbud</div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <a
                          href="#kontakt"
                          className="px-4 py-1.5 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white text-xs font-bold transition-colors inline-block"
                        >
                          Få tilbud
                        </a>
                      </td>
                    </tr>
                    <tr className="hover:bg-stone-50/80 transition-colors">
                      <td className="py-4 px-6">
                        <div className="font-bold text-stone-900">
                          Scanning af flade tage
                        </div>
                        <div className="text-xs text-stone-500">
                          Afhænger af tagfladens størrelse og adgang
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-extrabold text-stone-800 text-base">
                          Pris efter tilbud
                        </span>
                        <div className="text-xs text-stone-500">Skræddersyet tilbud</div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <a
                          href="#kontakt"
                          className="px-4 py-1.5 rounded-full bg-stone-100 hover:bg-stone-200 border border-stone-300 text-stone-800 text-xs font-bold transition-colors inline-block"
                        >
                          Indhent pris
                        </a>
                      </td>
                    </tr>
                    <tr className="hover:bg-stone-50/80 transition-colors">
                      <td className="py-4 px-6">
                        <div className="font-bold text-stone-900">
                          Fugtmåling sammen med termografi eller trykprøve
                        </div>
                        <div className="text-xs text-stone-500">
                          Udføres ved samme besøg – sparer ekstra kørsel
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="font-extrabold text-emerald-700 text-base">
                          Pris efter tilbud
                        </span>
                        <div className="text-xs text-emerald-600">Fordelagtig kombipris</div>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <a
                          href="#kontakt"
                          className="px-4 py-1.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition-colors inline-block shadow-2xs"
                        >
                          Få kombi-tilbud
                        </a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="p-4 bg-stone-50 border-t border-stone-200 text-xs text-stone-600">
                Du kender altid prisen, før vi går i gang. Er opgaven større end normalt, aftaler vi den endelige pris inden besøget. Rapporten sendes inden for 2–5 hverdage efter målingen.
              </div>
            </div>

            {/* CTA til professionelle (1.11) */}
            <div className="p-6 sm:p-8 rounded-2xl bg-white border border-stone-200 flex flex-col sm:flex-row items-center justify-between gap-6 shadow-2xs">
              <div className="space-y-1">
                <span className="px-2.5 py-0.5 rounded text-[11px] font-bold bg-stone-100 text-stone-700 border border-stone-200">
                  Til erhverv & fagfolk
                </span>
                <h4 className="text-lg font-bold text-stone-900">
                  Entreprenør, tømrer eller gulvlægger?
                </h4>
                <p className="text-xs sm:text-sm text-stone-600 max-w-xl">
                  Vi fugtmåler tage, vægge, gulve og træ på jeres byggepladser i Jylland og på Fyn – også sammen med trykprøven.
                </p>
              </div>
              <a
                href="#kontakt"
                className="px-6 py-2.5 rounded-full bg-[#165B4C] hover:bg-[#11463A] text-white text-xs font-bold transition-colors shrink-0"
              >
                Kontakt os for erhvervsaftale
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 8. HVORFOR VÆLGE ET DANMARK (1.10) */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Hvorfor vælge ET Danmark til fugtmålingen?
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Kombinationen af praktisk byggeerfaring og markedets mest avancerede måleudstyr giver dig den største tryghed:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="p-6 rounded-2xl bg-[#FAF9F6] border border-stone-200/80">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold text-sm mb-4">
                20+
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-2">
                20+ års erfaring i byggebranchen
              </h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Vi kender byggeriet indefra – fra mange års selvstændigt virke med totalentrepriser. Derfor ved vi, hvordan konstruktionerne er bygget op, og hvad der skal til for at udbedre en fugtskade.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-[#FAF9F6] border border-stone-200/80">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-sm mb-4">
                <i className="fa-solid fa-arrows-split-up-and-left" aria-hidden="true"></i>
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-2">
                Fugt, varme og tæthed set i sammenhæng
              </h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Vi laver også termografi og trykprøvning. Fugt hænger ofte sammen med utætheder og kuldebroer, og med flere målemetoder får du et mere sikkert billede af årsagen.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-[#FAF9F6] border border-stone-200/80">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-sm mb-4">
                <i className="fa-solid fa-map-location-dot" aria-hidden="true"></i>
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-2">
                Lokal i Jylland og på Fyn
              </h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Vi har base i Horsens og dækker hele Jylland og Fyn. Det sikrer kort vej, fleksibilitet og hurtig udrykning, hvis der opstår akutte vandskader.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-[#FAF9F6] border border-stone-200/80">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center text-sm mb-4">
                <i className="fa-solid fa-handshake" aria-hidden="true"></i>
              </div>
              <h3 className="text-base font-bold text-stone-900 mb-2">
                Ærlig og saglig vurdering
              </h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Vi fortæller dig hvad målingen kan vise – og hvad den ikke kan. Er der brug for en anden undersøgelse, siger vi det ærligt og hjælper dig videre.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FUGTMÅLING I DIT OMRÅDE (LOKALE SIDER) */}
      <section className="py-16 bg-stone-50 border-b border-stone-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 uppercase tracking-wider mb-2">
              <i className="fa-solid fa-location-dot text-emerald-600" aria-hidden="true"></i>
              Dækningsområde
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-stone-900 tracking-tight">
              Fugtmåling i dit område
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Vi dækker hele Jylland og Fyn. Se mere om vores lokale ydelser i de større byer:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Link
              to="/fugtmaaling-i-horsens"
              className="p-6 rounded-2xl bg-white border border-stone-200/80 hover:border-emerald-500 shadow-2xs hover:shadow-md transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-stone-900 text-base group-hover:text-[#165B4C] transition-colors">
                    Fugtmåling i Horsens
                  </h3>
                  <i className="fa-solid fa-arrow-right text-xs text-stone-400 group-hover:text-[#165B4C] group-hover:translate-x-1 transition-all" aria-hidden="true"></i>
                </div>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Vandskader, kældre og hurtig udrykning fra vores base på Bjerrevej i Horsens.
                </p>
              </div>
              <span className="text-[11px] font-semibold text-emerald-700 mt-4 block">
                Læs om Horsens &rarr;
              </span>
            </Link>

            <Link
              to="/fugtmaaling-i-aarhus"
              className="p-6 rounded-2xl bg-white border border-stone-200/80 hover:border-emerald-500 shadow-2xs hover:shadow-md transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-stone-900 text-base group-hover:text-[#165B4C] transition-colors">
                    Fugtmåling i Aarhus
                  </h3>
                  <i className="fa-solid fa-arrow-right text-xs text-stone-400 group-hover:text-[#165B4C] group-hover:translate-x-1 transition-all" aria-hidden="true"></i>
                </div>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Nybyggeri, betongulve, flade tage og rådgivning til entreprenører og bygherrer.
                </p>
              </div>
              <span className="text-[11px] font-semibold text-emerald-700 mt-4 block">
                Læs om Aarhus &rarr;
              </span>
            </Link>

            <Link
              to="/fugtmaaling-i-odense"
              className="p-6 rounded-2xl bg-white border border-stone-200/80 hover:border-emerald-500 shadow-2xs hover:shadow-md transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-stone-900 text-base group-hover:text-[#165B4C] transition-colors">
                    Fugtmåling i Odense
                  </h3>
                  <i className="fa-solid fa-arrow-right text-xs text-stone-400 group-hover:text-[#165B4C] group-hover:translate-x-1 transition-all" aria-hidden="true"></i>
                </div>
                <p className="text-xs text-stone-600 leading-relaxed">
                  Fyn, bolighandel, kældertjek og tagpapmåling til private boligejere.
                </p>
              </div>
              <span className="text-[11px] font-semibold text-emerald-700 mt-4 block">
                Læs om Odense & Fyn &rarr;
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* 10. SOCIAL PROOF (UDVALGTE ANMELDELSER) */}
      <ReviewsSlider />

      {/* 11. FAQ ACCORDION (1.12) */}
      <section className="py-16 sm:py-20 bg-white border-b border-stone-200/70" id="faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider mb-2">
              <i className="fa-solid fa-circle-question" aria-hidden="true"></i>
              <span>Spørgsmål & Svar</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Ofte stillede spørgsmål om fugtmåling
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Her finder du svar på de mest almindelige spørgsmål om Tramex-måling, metoder og priser.
            </p>
          </div>

          <div className="space-y-3">
            {fugtFaqs.map((faq, idx) => (
              <div
                key={idx}
                className="rounded-2xl border border-stone-200 bg-[#FAF9F6] overflow-hidden transition-all"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full py-4 px-6 text-left font-bold text-stone-900 flex items-center justify-between gap-4 text-sm sm:text-base hover:text-[#165B4C] transition-colors"
                  aria-expanded={openFaq === idx}
                >
                  <span>{faq.q}</span>
                  <i
                    className={`fa-solid fa-chevron-down text-xs text-stone-500 transition-transform duration-200 shrink-0 ${
                      openFaq === idx ? "rotate-180 text-[#165B4C]" : ""
                    }`}
                    aria-hidden="true"
                  ></i>
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5 text-stone-600 text-xs sm:text-sm leading-relaxed border-t border-stone-200/60 pt-3">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-stone-600 text-xs sm:text-sm">
              Har du et specifikt spørgsmål til dit byggeri?{" "}
              <a href="tel:+4542499595" className="text-[#165B4C] font-bold hover:underline">
                Ring direkte til Lars på 42 49 95 95
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* 12. KONTAKT FORMULAR */}
      <section className="py-16 sm:py-20 bg-stone-50" id="kontakt">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <span className="px-3 py-1 rounded-full bg-emerald-100 text-[#165B4C] text-xs font-bold uppercase tracking-wider mb-2 inline-block">
              Få et uforpligtende tilbud
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-stone-900 tracking-tight">
              Klar til at få styr på fugten?
            </h2>
            <p className="text-stone-600 text-sm mt-2">
              Udfyld formularen nedenfor og vælg "Fugtmåling", eller ring direkte på 42 49 95 95. Vi vender hurtigt tilbage!
            </p>
          </div>

          <div className="bg-white p-6 sm:p-10 rounded-3xl border border-stone-200 shadow-xl">
            <ContactForm
              defaultService="Fugtmåling"
              title="Indhent tilbud på fugtmåling"
              subtitle="Beskriv kort din opgave – fx hvad du har set af fugt, skjolder eller mistanke. Du er velkommen til at vedhæfte fotos."
            />
          </div>
        </div>
      </section>
    </div>
  );
}
